import { useNavigate } from "react-router"
import React, { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { ClosingTimeType, CreateRoomResource, CreateRoomResourceForm, IntegrationType } from "../../resourcesTypes"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { editResource, getResource, ServiceCenterResourcesQuery } from "../../resourcesApi"
import { enqueueSnackbar } from "notistack"
import {
    SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../../constants/routes"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import { Button } from "../../../../../../office/admin/components/Button"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../../shared/components/links/StandardBreadcrumbs"
import { CreateUpdateRoomForm } from "../../components/room/CreateUpdateRoomForm"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { SupportEntityQuery } from "../../../../../../support/admin/entities/supportEntityApi"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { DateTimeFactory, useDateTime } from "../../../../../../localization/useDateTime"
import { transformRoomFormToResource } from "./utils"

export function EditRoom() {
    const roomFormRef = useRef<FormikProps<CreateRoomResourceForm>>(null)

    const handleSaveClick = () => {
        roomFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="service-center-admin-organization-resources-create-room-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_EDIT_ROOM_ROUTE}
                    title="Edit room"
                    rightElement={
                        <LocalizedStrict id="support-create-company-update-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Update
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-resource-crate-room">
                            <Breadcrumb>Update room</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Room ref={roomFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const transformData = (data: CreateRoomResource, dateTimeFactory: DateTimeFactory): CreateRoomResourceForm => {
    const { bookingTime, closingTimes, integrations, ...rest } = data
    const closingTime = closingTimes.filter((i) => i.type === ClosingTimeType.DAILY)?.[0]?.periodInTime
    const activeIntegration = integrations[0]

    return {
        ...rest,
        closingTimes,
        integrationType: activeIntegration.type ?? IntegrationType.NONE,
        integrationEmailAddress: activeIntegration.emailAddress,
        reference: data?.reference ?? "",
        isClosingTimes: !!closingTime,
        isBookingTime: !!bookingTime,
        ...(bookingTime && {
            bookingTime: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime.from),
                    to: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime?.to),
                },
            },
        }),
        ...(closingTime && {
            closingTimesDaily: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(closingTime.from),
                    to: dateTimeFactory.fromHourMinute(closingTime.to),
                },
            },
        }),
    }
}

const Room = forwardRef<FormikProps<CreateRoomResourceForm>>((_props, ref) => {
    const dateTimeFactory = useDateTime()
    const navigate = useNavigate()
    const { roomId } = useParams()
    const roomIdNumber = Number(roomId)

    const roomDetails = useApiQuery(getResource, {
        queryName: SupportEntityQuery.DETAILS,
        dependencies: {
            roomId: roomIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(roomId),
        select: (data) => transformData(data, dateTimeFactory),
    })

    const { mutate } = useApiMutation(editResource, {
        async onSuccess(_, data, context) {
            navigate(SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE)
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterResourcesQuery.SERVICE_CENTER_RESOURCES],
            })
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const editRoom = (value: CreateRoomResourceForm) => {
        if (!roomId) throw new Error("Room ID is undefined")

        const updatedForm = transformRoomFormToResource(value, dateTimeFactory)

        mutate({
            roomId: roomIdNumber,
            ...updatedForm,
        })
    }

    return <CreateUpdateRoomForm initialValues={roomDetails.data} ref={ref} onSubmit={editRoom} />
})
